"use client";

import type { AdminDashMembers } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { adminDashMembersSchema } from "../../../../lib/types/schema/community/members/adminDashMembersSchema";
import { getRequestNextPageParam } from "../../../../lib/utils/getRequestNextPageParam";
import type { UseRequestInfiniteQueryProps } from "../../../../request/hooks/useRequestInfiniteQuery";
import { useRequestInfiniteQuery } from "../../../../request/hooks/useRequestInfiniteQuery";

export const KEY_ADMIN_STAFF_CANDIDATES_BASE =
  "admin-staff-candidates" as const;

const ADMIN_STAFF_CANDIDATES_PAGE_SIZE = 50;

export type UseRequestAdminStaffCandidatesProps =
  UseRequestInfiniteQueryProps & {
    /**
     * Class community id. The backend resolves the parent school(s) and returns
     * their staff (admins or teachers), already excluding anyone who is staff of
     * or pending in this class.
     */
    id: number;
    search?: string;
  };

/**
 * Candidates eligible to be added as staff (admins) of a class.
 *
 * Backend contract (hallow-inc/backend):
 * - GET /communities/{id}/admin/staff/candidates?page&per_page&search
 *   where {id} is the CLASS community id. Returns the standard
 *   AdminDashboardUsersResponse ({ results, total, all_user_ids,
 *   non_admin_user_ids }) with the class's current staff and pending users
 *   already excluded server-side.
 *
 * This endpoint is not in the generated OpenAPI schema, so it uses the
 * request-based infinite-query pattern (mirrors useRequestCommunitiesAdminClasses
 * and the sibling useRequestAdminDashboardStaff mutations).
 */
export const useRequestAdminStaffCandidates = ({
  id,
  search,
  ...props
}: UseRequestAdminStaffCandidatesProps) => ({
  infiniteQuery: useRequestInfiniteQuery<typeof adminDashMembersSchema>({
    getNextPageParam: (
      lastPage: AdminDashMembers,
      _allPages: AdminDashMembers[],
      lastPageParam: number,
    ) =>
      getRequestNextPageParam({
        total: lastPage.total,
        lastPageParam,
        pageSize: ADMIN_STAFF_CANDIDATES_PAGE_SIZE,
      }),
    initialPageParam: 0,
    placeholderData: (previousData) => previousData,
    queryFnRequestProps: ({ pageParam }) => ({
      schema: adminDashMembersSchema,
      urlBackendPath: `/communities/${id}/admin/staff/candidates?page=${pageParam}&per_page=${ADMIN_STAFF_CANDIDATES_PAGE_SIZE}&search=${encodeURIComponent(search ?? "")}`,
    }),
    enabledWithoutSessionToken: false,
    queryKey: [KEY_ADMIN_STAFF_CANDIDATES_BASE, id.toString(), search ?? ""],
    ...props,
  }),
});
